<template>
    
    <div class="e-ribbon__blocks-wrapper" @mouseenter="pauseScroll" @mouseleave="resumeScroll" ref="marqueeContent" :class="{ 'is-ready': isReady }">
        <div class="e-ribbon__blocks-track" ref="marqueeTrack">
            
            <!-- Wrap the v-for loop inside transition-group to apply animations -->
            <transition-group 
                v-if="profiles.length > 0"
                name="fade" 
                tag="div" 
                class="e-ribbon__blocks-transition-group" 
                :css="false"
            >
        
                <div 
                    v-for="(profile, index) in duplicateProfiles"
                    :key="index + profile.id" 
                    class="e-ribbon__block e-ribbon__block--profile"
                    :style="{ animationDelay: `${index * 0.4}s` }"
                >
                    <div class="e-ribbon__block-image-wrapper">
                        <div class="e-ribbon__block-image-liner">
                            <img 
                                class="e-ribbon__block-image" 
                                :src="profile.image" 
                                :alt="profile.title" 
                                loading="lazy" 
                                width="180" 
                                height="180">
                        </div>
                    </div>

                    <div class="e-ribbon__block-content">
                        <h3 class="e-ribbon__block-title">{{ profile.title }}</h3>
                        <p class="e-ribbon__block-description">{{ profile.description }}</p>
                        <a v-if="profile.link" :href="profile.link" class="e-ribbon__block-link">
                            <span class="e-ribbon__block-link-text">View profile</span>
                        </a>
                    </div>
                </div>

            </transition-group>

            <!-- Wrap the v-for loop inside transition-group to apply animations -->
            <transition-group 
                v-if="votings.length > 0"
                name="fade"
                tag="div" 
                class="e-ribbon__blocks-transition-group" 
                :css="false"
            >
                <div 
                    v-for="(item, index) in duplicateVotingItems" 
                    :key="index + item.id"
                    class="e-ribbon__block e-ribbon__block--vote"
                    :style="{ animationDelay: `${index * 0.4}s` }"
                >
                    <div class="e-ribbon__block-image-wrapper">
                        <div class="e-ribbon__block-image-liner">
                            <img 
                                class="e-ribbon__block-image" 
                                :src="item.image" 
                                :alt="item.title" 
                                loading="lazy" 
                                width="180" 
                                height="180">
                        </div>
                    </div>

                    <div class="e-ribbon__block-content">
                        <h3 class="e-ribbon__block-title">{{ item.title }}</h3>
                        <p class="e-ribbon__block-description">{{ item.description }}</p>
                        
                        <h4 class="e-ribbon__block-vote">Voted: 
                            <span 
                                class="e-ribbon__block-vote-pill" 
                                :class="'vote-' + item.voteForAgainst.toLowerCase()" 
                                v-html="getVoteSVG(item.voteForAgainst)">                                    
                            </span>
                        </h4>
                        
                        <a v-if="item.link" :href="item.link" class="e-ribbon__block-link">
                            <span class="e-ribbon__block-link-text">{{ item.linkTitle }}</span>
                        </a>
                    </div>
                </div>

            </transition-group>

        </div>
    </div>

</template>
  
<script>
    export default {
        props: {
            profiles: {
                type: Array,
                required: false,
                default: () => []
            },
            votings: {
                type: Array,
                required: false,
                default: () => []
            },
            speed: {
                type: Number,
                default: 45, // Adjust speed (lower is faster)
            }
        },
        data() {
            return {
                isPaused: false,
                scrollPosition: 0,
                animationFrame: null,
                isReady: false
            };
        },
        computed: {
            // This will return the duplicated items for continuous scrolling
            duplicateProfiles() {
                return [...this.profiles, ...this.profiles]; // Duplicate the profiles array
            },
            duplicateVotingItems() {
                return [...this.votings, ...this.votings]; // Duplicate the votings array
            }
        },
        mounted() {
            this.$nextTick(() => {
                this.isReady = true;  // Only show after the component is fully ready
                this.scrollPosition = 300;  // Set initial scroll position offset
                this.startScrolling();
            });
        },
        methods: {
            startScrolling() {
                const marqueeTrack = this.$refs.marqueeTrack;
                if (!marqueeTrack) return;
        
                const totalWidth = marqueeTrack.scrollWidth / 2;
                const scrollStep = 30 / this.speed;
        
                const scroll = () => {
                if (this.isPaused) return;
        
                this.scrollPosition -= scrollStep;
                marqueeTrack.style.transform = `translateX(${this.scrollPosition}px)`;
        
                if (Math.abs(this.scrollPosition) >= totalWidth) {
                    this.scrollPosition = 0;
                }
        
                this.animationFrame = requestAnimationFrame(scroll);
                };
        
                scroll();
            },
            pauseScroll() {
                this.isPaused = true;
                cancelAnimationFrame(this.animationFrame);
            },
            resumeScroll() {
                this.isPaused = false;
                this.startScrolling();
            },
            getVoteSVG(voteType) {
                if (voteType === 'For') {
                    return `
                        <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
                            <path d="M20.5 7.28003L9.85355 17.9265C9.65829 18.1217 9.34171 18.1217 9.14645 17.9265L4.5 13.28" stroke="white" stroke-width="2" stroke-linecap="round"/>
                        </svg>`;
                } else if (voteType === 'Against') {
                    return `
                        <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
                            <path d="M18.5 6.28003L6.5 18.28M18.5 18.28L6.5 6.28004" stroke="white" stroke-width="2" stroke-linecap="round"/>
                        </svg>`;
                }
                return ''; // Return empty string if no valid voteType
            }
        }
    };
</script>

<style scoped>
    /* Staggered fade-in effect */
    .fade-enter-active, .fade-leave-active {
        transition: opacity 0.5s ease-in-out, transform 0.5s ease-in-out;
    }

    .fade-enter, .fade-leave-to /* .fade-leave-active in <2.1.8 */ {
        opacity: 0;
        transform: translateY(20px); /* Slide up effect */
    }
</style>